import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Slide,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {TransitionProps} from '@mui/material/transitions';
import {PolicyApplicationRecordInfoDTO} from 'Agent/Policies/api';
import {AsyncPage, Loaded, toPage} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import React, {forwardRef, ReactElement, Ref} from 'react';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import Column from 'Shared/components/PagedView/Column';
import PagedView from 'Shared/components/PagedView/PagedView';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {toMoneyValue} from 'Shared/utilities/FormatMoney';
import {NoOp} from 'Shared/utilities/IOUtilities';

const useStyles = makeStyles(_theme => ({
  dialog: {
    '& .MuiDialog-paperFullWidth': {
      maxHeight: '80%',
      height: '80%',
    },
  },
  dialogTitle: {
    backgroundColor: '#fff',
    color: '#0033A0',
    fontSize: '1rem',
    fontWeight: 700,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem',
    paddingLeft: '2.5rem',
    maxHeight: '12%',
    borderBottom: '1px solid #ccc',
  },
  dialogContent: {
    '& .paged-table__paper': {
      boxShadow: 'none',
      height: '100%',
    },
    '& .paged-table__header-row': {
      boxShadow: 'none',
      borderBottom: '2px solid #ccc',
    },
    '& .paged-table__footer': {
      boxShadow: 'none',
    },
  },
  enrollmentGrid: {
    borderBottom: '2px solid #ccc',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
}));

type EnrollmentDetailsProps = {
  enrollmentDetails: AsyncPage<Page<PolicyApplicationRecordInfoDTO>>;
};

const EnrollmentDetailsDesktop = ({enrollmentDetails}: EnrollmentDetailsProps) => {
  return (
    <PagedView pagedViewKey="enrollmentDetails" data={enrollmentDetails} loadMore={NoOp}>
      {columnProps => (
        <>
          <Column {...columnProps} title="Insured Name" default ddprivacy="mask">
            {enrollment => enrollment.insuredName}
          </Column>
          <Column {...columnProps} title="Insured Last 4-SSN" default ddprivacy="mask">
            {enrollment => enrollment.insuredIdentifier}
          </Column>
          <Column {...columnProps} title="Writing Agent Name" ddprivacy="mask">
            {enrollment => enrollment.writingAgent}
          </Column>
          <Column {...columnProps} title="Writing Agent Number" ddprivacy="mask">
            {enrollment => enrollment.writingAgentNumber}
          </Column>
          <Column {...columnProps} title="Plan Name" default ddprivacy="mask">
            {enrollment => enrollment.planName}
          </Column>
          <Column {...columnProps} title="Relationship" ddprivacy="mask">
            {enrollment => enrollment.relation}
          </Column>
          <Column {...columnProps} title="Premium Mode" ddprivacy="mask">
            {enrollment => enrollment.premiumMode}
          </Column>
          <Column {...columnProps} title="Deduction Amount" default ddprivacy="mask">
            {enrollment => toMoneyValue(enrollment.deductionAmount)}
          </Column>
          <Column {...columnProps} title="Annual Premium" default ddprivacy="mask">
            {enrollment => toMoneyValue(enrollment.annualPremium)}
          </Column>
          <Column {...columnProps} title="Date App Signed" default ddprivacy="mask">
            {enrollment => toFormattedDateAsUTC(enrollment.dateAppSigned)}
          </Column>
          <Column {...columnProps} title="Submission Date" default ddprivacy="mask">
            {enrollment => toFormattedDateAsUTC(enrollment.submissionDate)}
          </Column>
          <Column {...columnProps} title="Original Date" ddprivacy="mask">
            {enrollment => toFormattedDateAsUTC(enrollment.originalDate)}
          </Column>
          <Column {...columnProps} title="Status" default ddprivacy="mask">
            {enrollment => enrollment.status?.name}
          </Column>
        </>
      )}
    </PagedView>
  );
};

const SlideTransition = forwardRef((props: TransitionProps & {children?: ReactElement}, ref: Ref<unknown>) => (
  <Slide direction="up" ref={ref} {...props} />
));

const EnrollmentDetailsMobile = ({enrollmentDetails}: EnrollmentDetailsProps) => {
  const styles = useStyles();
  return (
    <Box>
      {enrollmentDetails.key === 'Loaded' &&
        enrollmentDetails.Data.result.map(enrollment => (
          <Grid container spacing={2} className={styles.enrollmentGrid}>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Insured Name" value={enrollment.insuredName} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Insured Last 4-SSN" value={enrollment.insuredIdentifier} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Writing Agent Name" value={enrollment.writingAgent} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Writing Agent Number" value={enrollment.writingAgentNumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Plan Name" value={enrollment.planName} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Relationship" value={enrollment.relation} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Premium Mode" value={enrollment.premiumMode} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Deduction Amount" value={toMoneyValue(enrollment.deductionAmount)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Annual Premium" value={toMoneyValue(enrollment.annualPremium)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Date App Signed" value={toFormattedDateAsUTC(enrollment.dateAppSigned)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Submission Date" value={toFormattedDateAsUTC(enrollment.submissionDate)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Original Date" value={toFormattedDateAsUTC(enrollment.originalDate)} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardFieldV2 name="Status" value={enrollment.status?.name} />
            </Grid>
          </Grid>
        ))}
    </Box>
  );
};


type EnrollmentDetailModalProps = {
  onClose: () => void;
  recordInfo: PolicyApplicationRecordInfoDTO[];
};

const EnrollmentDetailsModal = ({onClose, recordInfo}: EnrollmentDetailModalProps) => {
  const styles = useStyles();
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('lg'));

  const enrollmentDetails: AsyncPage<Page<PolicyApplicationRecordInfoDTO>> = new Loaded(toPage(recordInfo));
  
  return (
    <Dialog
      aria-labelledby="desktop-dialog-title"
      className={isDesktop ? styles.dialog : ''}
      disablePortal
      fullScreen={isDesktop ? false : true}
      fullWidth={true}
      maxWidth={isDesktop ? 'xl' : false}
      onClose={onClose}
      open
      TransitionComponent={isDesktop ? undefined : SlideTransition}
    >
      <DialogTitle disableTypography className={styles.dialogTitle}>
        <Typography variant="h6">Enrollment Details</Typography>
        {onClose ? (
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon style={{color: '#fff'}} />
          </IconButton>
        ) : null}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        {isDesktop ? (
          <EnrollmentDetailsDesktop enrollmentDetails={enrollmentDetails} />
        ) : (
          <EnrollmentDetailsMobile enrollmentDetails={enrollmentDetails} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentDetailsModal;
